import { useState, useEffect, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import { useRequirement, useUpdateRequirement } from '../../hooks/useRequirements';
import { REQUIREMENT_STATUS_LABELS, REQUIREMENT_CATEGORY_LABELS, PRIORITY_LABELS } from '@maios/shared';
import toast from 'react-hot-toast';
import axios from 'axios';
import Spinner from '../ui/Spinner';

// --- 상태 전이 규칙 ---
const REQUIREMENT_TRANSITIONS: Record<string, string[]> = {
  DRAFT: ['REVIEW', 'DEPRECATED'],
  REVIEW: ['APPROVED', 'DRAFT'],
  APPROVED: ['DEPRECATED'],
  DEPRECATED: [],
};

// --- 배지 색상 ---
const CATEGORY_COLOR: Record<string, string> = {
  FUNCTIONAL: 'bg-indigo-100 text-indigo-700',
  NON_FUNCTIONAL: 'bg-purple-100 text-purple-700',
  CONSTRAINT: 'bg-orange-100 text-orange-700',
};

// ============================================================================
// 동적 필드 설정 (REQ-V28-007 ~ REQ-V28-011)
// ============================================================================

const QUALITY_ATTRIBUTE_OPTIONS = [
  { value: 'PERFORMANCE', label: '성능' },
  { value: 'SECURITY', label: '보안' },
  { value: 'AVAILABILITY', label: '가용성' },
  { value: 'SCALABILITY', label: '확장성' },
  { value: 'MAINTAINABILITY', label: '유지보수성' },
];

const CONSTRAINT_TYPE_OPTIONS = [
  { value: 'TECHNOLOGY', label: '기술' },
  { value: 'REGULATION', label: '법규' },
  { value: 'BUDGET', label: '예산' },
  { value: 'SCHEDULE', label: '일정' },
  { value: 'SECURITY', label: '보안' },
];

interface FieldConfig {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'select';
  placeholder?: string;
  options?: { value: string; label: string }[];
}

const DYNAMIC_FIELD_CONFIG: Record<string, FieldConfig[]> = {
  FUNCTIONAL: [
    { key: 'actor', label: '액터', type: 'text', placeholder: '예: 일반사용자, 관리자' },
    { key: 'precondition', label: '사전 조건', type: 'text', placeholder: '기능 실행 전 조건' },
    { key: 'acceptCriteria', label: '수용 기준', type: 'textarea', placeholder: 'Given [사전 조건]\nWhen [행위]\nThen [기대 결과]' },
    { key: 'relatedScreen', label: '관련 화면/API', type: 'text', placeholder: '/path 또는 API 엔드포인트' },
  ],
  NON_FUNCTIONAL: [
    { key: 'qualityAttribute', label: '품질 속성', type: 'select', options: QUALITY_ATTRIBUTE_OPTIONS },
    { key: 'metric', label: '측정 지표', type: 'text', placeholder: '예: 응답시간 <1초, 가용성 99.9%' },
    { key: 'measureMethod', label: '측정 방법', type: 'text', placeholder: '예: 부하 테스트, 모니터링' },
  ],
  CONSTRAINT: [
    { key: 'constraintType', label: '제약 유형', type: 'select', options: CONSTRAINT_TYPE_OPTIONS },
    { key: 'rationale', label: '근거', type: 'text', placeholder: '왜 이 제약이 존재하는지' },
    { key: 'impactScope', label: '영향 범위', type: 'text', placeholder: '어떤 기능이나 영역에 영향을 주는지' },
  ],
};

const SECTION_TITLES: Record<string, string> = {
  FUNCTIONAL: '기능 상세',
  NON_FUNCTIONAL: '비기능 상세',
  CONSTRAINT: '제약사항 상세',
};

// ============================================================================
// DynamicFieldRow — 개별 동적 필드 인라인 편집
// ============================================================================

interface DynamicFieldRowProps {
  field: FieldConfig;
  value: any;
  onChange: (value: any) => void;
}

function DynamicFieldRow({ field, value, onChange }: DynamicFieldRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value ?? '');

  useEffect(() => {
    setLocalValue(value ?? '');
  }, [value]);

  const handleSave = () => {
    setIsEditing(false);
    if (localValue !== (value ?? '')) {
      onChange(localValue);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setLocalValue(value ?? '');
  };

  // select 필드
  if (field.type === 'select') {
    return (
      <div className="flex items-start gap-3 py-2">
        <span className="text-sm text-gray-500 w-[100px] flex-shrink-0">{field.label}</span>
        <select
          className="text-sm text-gray-900 flex-1 border border-gray-200 rounded px-2 py-1 focus:ring-1 focus:ring-primary-500 outline-none cursor-pointer hover:border-gray-300"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">선택</option>
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // textarea 필드
  if (field.type === 'textarea') {
    return (
      <div className="flex items-start gap-3 py-2">
        <span className="text-sm text-gray-500 w-[100px] flex-shrink-0 pt-1">{field.label}</span>
        {isEditing ? (
          <textarea
            className="text-sm text-gray-900 flex-1 border border-primary-300 rounded px-2 py-1 min-h-[80px] focus:outline-none focus:ring-1 focus:ring-primary-500 resize-y"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={(e) => {
              if (e.key === 'Escape') handleCancel();
            }}
            placeholder={field.placeholder}
            autoFocus
          />
        ) : (
          <span
            className={`text-sm flex-1 cursor-pointer whitespace-pre-wrap rounded px-2 py-1 hover:bg-gray-50 ${value ? 'text-gray-900' : 'text-gray-400 italic'}`}
            onClick={() => setIsEditing(true)}
          >
            {value || '미입력'}
          </span>
        )}
      </div>
    );
  }

  // text 필드 (기본)
  return (
    <div className="flex items-start gap-3 py-2">
      <span className="text-sm text-gray-500 w-[100px] flex-shrink-0">{field.label}</span>
      {isEditing ? (
        <input
          type="text"
          className="text-sm text-gray-900 flex-1 border border-primary-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') handleCancel();
          }}
          placeholder={field.placeholder}
          autoFocus
        />
      ) : (
        <span
          className={`text-sm flex-1 cursor-pointer rounded px-2 py-1 hover:bg-gray-50 ${value ? 'text-gray-900' : 'text-gray-400 italic'}`}
          onClick={() => setIsEditing(true)}
        >
          {value || '미입력'}
        </span>
      )}
    </div>
  );
}

// ============================================================================
// DynamicFieldsSection — 카테고리별 동적 필드 영역
// ============================================================================

interface DynamicFieldsSectionProps {
  category: string;
  dynamicFields: Record<string, any>;
  onFieldChange: (key: string, value: any) => void;
}

function DynamicFieldsSection({ category, dynamicFields, onFieldChange }: DynamicFieldsSectionProps) {
  const fields = DYNAMIC_FIELD_CONFIG[category];
  if (!fields || fields.length === 0) return null;

  return (
    <div className="border-t border-gray-200 pt-4 mt-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">{SECTION_TITLES[category]}</h3>
      <div className="space-y-1">
        {fields.map((field) => (
          <DynamicFieldRow
            key={field.key}
            field={field}
            value={dynamicFields[field.key] ?? ''}
            onChange={(value) => onFieldChange(field.key, value)}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// RequirementDetailPanel — 메인 사이드 패널
// ============================================================================

interface RequirementDetailPanelProps {
  requirementId: string;
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
  members: Array<{ user: { id: string; name: string } }>;
}

export default function RequirementDetailPanel({
  requirementId,
  projectId,
  isOpen,
  onClose,
  onUpdate,
  members,
}: RequirementDetailPanelProps) {
  const { data: requirement, isLoading } = useRequirement(projectId, requirementId);
  const updateMutation = useUpdateRequirement(projectId);

  // 슬라이드 인 애니메이션
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    // 마운트 후 다음 프레임에서 슬라이드 인 트리거
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // 인라인 편집 상태
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  // requirement 데이터 변경 시 로컬 상태 동기화
  useEffect(() => {
    if (requirement) {
      setEditTitle(requirement.title);
      setEditDescription(requirement.description ?? '');
    }
  }, [requirement]);

  // 편집 필드 포커스
  useEffect(() => {
    if (editingField === 'title') titleInputRef.current?.focus();
    if (editingField === 'description') descriptionRef.current?.focus();
  }, [editingField]);

  // API 호출 유틸
  const patchField = useCallback(
    async (data: Record<string, any>) => {
      if (!requirement) return;
      try {
        await updateMutation.mutateAsync({ reqId: requirement.id, data });
        onUpdate?.();
      } catch (err) {
        if (axios.isAxiosError(err)) {
          toast.error(err.response?.data?.error ?? '수정에 실패했습니다.');
        } else {
          toast.error('수정에 실패했습니다.');
        }
      }
    },
    [requirement, updateMutation, onUpdate],
  );

  // --- 제목 저장 ---
  const handleTitleSave = useCallback(() => {
    setEditingField(null);
    if (!requirement || editTitle.trim() === requirement.title) return;
    if (!editTitle.trim()) {
      setEditTitle(requirement.title);
      return;
    }
    patchField({ title: editTitle.trim() });
  }, [requirement, editTitle, patchField]);

  // --- 설명 저장 ---
  const handleDescriptionSave = useCallback(() => {
    setEditingField(null);
    if (!requirement) return;
    const newDesc = editDescription.trim();
    const oldDesc = requirement.description ?? '';
    if (newDesc === oldDesc) return;
    patchField({ description: newDesc || null });
  }, [requirement, editDescription, patchField]);

  // --- 카테고리 변경 ---
  const handleCategoryChange = useCallback(
    (newCategory: string) => {
      if (!requirement || newCategory === requirement.category) return;
      const confirmed = confirm('카테고리를 변경하면 기존 세부 필드가 초기화됩니다. 계속하시겠습니까?');
      if (!confirmed) return;
      patchField({ category: newCategory, dynamicFields: {} });
    },
    [requirement, patchField],
  );

  // --- 우선순위 변경 ---
  const handlePriorityChange = useCallback(
    (newPriority: string) => {
      if (!requirement || newPriority === requirement.priority) return;
      patchField({ priority: newPriority });
    },
    [requirement, patchField],
  );

  // --- 상태 변경 ---
  const handleStatusChange = useCallback(
    (newStatus: string) => {
      if (!requirement || newStatus === requirement.status) return;
      const allowed = REQUIREMENT_TRANSITIONS[requirement.status] ?? [];
      if (!allowed.includes(newStatus)) {
        toast.error(
          `상태를 ${REQUIREMENT_STATUS_LABELS[requirement.status]}에서 ${REQUIREMENT_STATUS_LABELS[newStatus]}(으)로 변경할 수 없습니다.`,
        );
        return;
      }
      patchField({ status: newStatus });
    },
    [requirement, patchField],
  );

  // --- 담당자 변경 ---
  const handleAssigneeChange = useCallback(
    (newAssigneeId: string) => {
      if (!requirement) return;
      const current = requirement.assigneeId ?? '';
      if (newAssigneeId === current) return;
      patchField({ assigneeId: newAssigneeId || null });
    },
    [requirement, patchField],
  );

  // --- 동적 필드 변경 (REQ-V28-005: 머지 방식) ---
  const handleDynamicFieldChange = useCallback(
    (key: string, value: any) => {
      patchField({ dynamicFields: { [key]: value } });
    },
    [patchField],
  );

  // 허용된 상태 전이 목록
  const allowedStatuses = requirement
    ? [requirement.status, ...(REQUIREMENT_TRANSITIONS[requirement.status] ?? [])]
    : [];

  if (!isOpen) return null;

  return (
    <>
      {/* 오버레이 */}
      <div className="fixed inset-0 bg-black/20 z-30" onClick={onClose} />

      {/* 사이드 패널 */}
      <div
        className={`fixed top-0 right-0 h-full w-[480px] bg-white shadow-xl z-40
          transform transition-transform duration-300
          ${mounted ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {isLoading || !requirement ? (
          <div className="flex items-center justify-center h-full">
            <Spinner className="size-8" />
          </div>
        ) : (
          <>
            {/* 패널 헤더 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 font-mono">{requirement.reqId}</span>
                <span
                  className={`px-2 py-0.5 text-sm rounded-full font-medium ${CATEGORY_COLOR[requirement.category] ?? 'bg-gray-100 text-gray-700'}`}
                >
                  {REQUIREMENT_CATEGORY_LABELS[requirement.category] ?? requirement.category}
                </span>
                {requirement.version > 1 && (
                  <span className="text-[11px] text-gray-400">v{requirement.version}</span>
                )}
              </div>
              <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* 패널 본문 (스크롤) */}
            <div className="overflow-y-auto h-[calc(100%-64px)] px-6 py-4 space-y-5">
              {/* 제목 - 인라인 편집 */}
              <div>
                {editingField === 'title' ? (
                  <input
                    ref={titleInputRef}
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onBlur={handleTitleSave}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleTitleSave();
                      if (e.key === 'Escape') {
                        setEditTitle(requirement.title);
                        setEditingField(null);
                      }
                    }}
                    className="w-full text-lg font-semibold text-gray-900 border border-primary-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                ) : (
                  <h2
                    className="text-lg font-semibold text-gray-900 cursor-pointer hover:bg-gray-50 rounded-lg px-3 py-1.5 -mx-3"
                    onClick={() => setEditingField('title')}
                  >
                    {requirement.title}
                  </h2>
                )}
              </div>

              {/* 기본 필드 그리드 */}
              <div className="space-y-3">
                {/* 카테고리 */}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500 w-[80px] flex-shrink-0">카테고리</span>
                  <select
                    value={requirement.category}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1 focus:ring-2 focus:ring-primary-500 outline-none cursor-pointer hover:border-gray-300"
                  >
                    {Object.entries(REQUIREMENT_CATEGORY_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 우선순위 */}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500 w-[80px] flex-shrink-0">우선순위</span>
                  <select
                    value={requirement.priority}
                    onChange={(e) => handlePriorityChange(e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1 focus:ring-2 focus:ring-primary-500 outline-none cursor-pointer hover:border-gray-300"
                  >
                    {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 상태 */}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500 w-[80px] flex-shrink-0">상태</span>
                  <select
                    value={requirement.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1 focus:ring-2 focus:ring-primary-500 outline-none cursor-pointer hover:border-gray-300"
                  >
                    {Object.entries(REQUIREMENT_STATUS_LABELS).map(([k, v]) => (
                      <option key={k} value={k} disabled={!allowedStatuses.includes(k)}>
                        {v}
                        {!allowedStatuses.includes(k) ? ' (전이 불가)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 담당자 */}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500 w-[80px] flex-shrink-0">담당자</span>
                  <select
                    value={requirement.assigneeId ?? ''}
                    onChange={(e) => handleAssigneeChange(e.target.value)}
                    className="text-sm border border-gray-200 rounded-lg px-2 py-1 focus:ring-2 focus:ring-primary-500 outline-none cursor-pointer hover:border-gray-300"
                  >
                    <option value="">미할당</option>
                    {members.map((m) => (
                      <option key={m.user.id} value={m.user.id}>
                        {m.user.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 설명 - 인라인 편집 */}
              <div className="border-t border-gray-200 pt-4">
                <span className="text-sm font-medium text-gray-700 mb-2 block">설명</span>
                {editingField === 'description' ? (
                  <textarea
                    ref={descriptionRef}
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    onBlur={handleDescriptionSave}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        setEditDescription(requirement.description ?? '');
                        setEditingField(null);
                      }
                    }}
                    className="w-full text-sm text-gray-900 border border-primary-300 rounded-lg px-3 py-2 min-h-[100px] focus:ring-2 focus:ring-primary-500 outline-none resize-y"
                  />
                ) : (
                  <div
                    className="text-sm text-gray-900 cursor-pointer hover:bg-gray-50 rounded-lg px-3 py-2 -mx-3 min-h-[40px] whitespace-pre-wrap"
                    onClick={() => setEditingField('description')}
                  >
                    {requirement.description || (
                      <span className="text-gray-400 italic">미입력</span>
                    )}
                  </div>
                )}
              </div>

              {/* 카테고리별 동적 필드 섹션 (REQ-V28-007 ~ REQ-V28-010) */}
              <DynamicFieldsSection
                category={requirement.category}
                dynamicFields={(requirement.dynamicFields as Record<string, any>) || {}}
                onFieldChange={handleDynamicFieldChange}
              />

              {/* 메타 정보 */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center gap-3 py-1">
                  <span className="text-sm text-gray-400 w-[100px]">생성일</span>
                  <span className="text-sm text-gray-500">
                    {new Date(requirement.createdAt).toLocaleDateString('ko-KR')}
                  </span>
                </div>
                <div className="flex items-center gap-3 py-1">
                  <span className="text-sm text-gray-400 w-[100px]">수정일</span>
                  <span className="text-sm text-gray-500">
                    {new Date(requirement.updatedAt).toLocaleDateString('ko-KR')}
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
